// @flow
import { connect, Provider } from 'react-redux';

import { LandingPad } from '@components/Portal';

import DyorDataWrapper from '@components/DyorDataWrapper';
import LumeraPersistGate from '@components/LumeraPersistGate';
import DYORServerSyncGate from '@components/DYORServerSyncGate';

import Setting from './Setting';
import Diamond from './Diamond';
import Combined from './Combined';

import css from '@styles/dyor';

import type { DyorDiamondType } from '@types/diamond';
import type { DyorSettingType } from '@types/setting';
import type { ObjectType } from '@types/misc';

type PropsType = {
  diamond: ?DyorDiamondType,
  setting: ?DyorSettingType,
  firstStep?: ?string,
  activeStep?: ?string,
  style: ObjectType
};

function Container({ diamond, setting, firstStep, style }: PropsType) {
  const containerRef = React.useRef(null);
  const [visibleDetails, setVisibleDetails] = React.useState(null);
  const location = currentLocation();
  const isOnDiamondPage =
    location && location.match(/^\/diamonds\/(?:search|detail)/);
  const isOnSettingPage =
    location && location.match(/^\/engagement-rings\/(?:search|detail)/);
  const isOnRingPage = location && location.match(/^\/your-ring/);
  const activeStep =
    (isOnDiamondPage && 'diamond') ||
    (isOnSettingPage && 'setting') ||
    (isOnRingPage && 'ring');

  const hideAllDetails = React.useCallback(() => {
    setVisibleDetails(null);
  });

  // If there is nothing set, show setting as the first step
  if (firstStep === 'diamond') {
    return (
      <div className={css.container} style={style} ref={containerRef}>
        <Diamond
          diamond={diamond}
          active={activeStep === 'diamond'}
          detailsAreVisible={visibleDetails === 'diamond'}
          setVisibleDetails={setVisibleDetails}
          containerRef={containerRef}
          chainPosition={1}
        />
        <Setting
          setting={setting}
          active={activeStep === 'setting'}
          detailsAreVisible={visibleDetails === 'setting'}
          setVisibleDetails={setVisibleDetails}
          containerRef={containerRef}
          chainPosition={2}
        />
        <Combined
          diamond={diamond}
          setting={setting}
          active={activeStep === 'ring'}
          onClick={hideAllDetails}
        />
        <LandingPad htmlId='dyor-detail-popover' />
      </div>
    );
  } else {
    return (
      <div className={css.container} style={style} ref={containerRef}>
        <Setting
          setting={setting}
          active={activeStep === 'setting'}
          detailsAreVisible={visibleDetails === 'setting'}
          setVisibleDetails={setVisibleDetails}
          containerRef={containerRef}
          chainPosition={1}
        />
        <Diamond
          diamond={diamond}
          active={activeStep === 'diamond'}
          detailsAreVisible={visibleDetails === 'diamond'}
          setVisibleDetails={setVisibleDetails}
          containerRef={containerRef}
          chainPosition={2}
        />
        <Combined
          diamond={diamond}
          setting={setting}
          active={activeStep === 'ring'}
          onClick={hideAllDetails}
        />
        <LandingPad htmlId='dyor-detail-popover' />
      </div>
    );
  }
}

function currentLocation() {
  return window && window.location && window.location.pathname;
}

function defaultFirstStep() {
  const location = currentLocation();
  const isOnDiamondPage = location && location.match(/^\/diamonds/);
  return isOnDiamondPage ? 'diamond' : 'setting';
}

function mapStateToProps(state, ownProps) {
  return {
    firstStep: state.ringBuilder.firstStep || defaultFirstStep(),
    diamond: state.ringBuilder.diamond.data,
    setting: state.ringBuilder.setting.data
  };
}

const ConnectedContainer = connect(mapStateToProps)(Container);

export default (props: { withWrapper?: boolean }) => {
  const { withWrapper, ...otherProps } = props;
  const store = ReactOnRails.getStore('SharedReduxStore');

  if (withWrapper) {
    return (
      <Provider store={store}>
        <DyorDataWrapper>
          <LumeraPersistGate>
            <DYORServerSyncGate>
              <ConnectedContainer {...otherProps} />
            </DYORServerSyncGate>
          </LumeraPersistGate>
        </DyorDataWrapper>
      </Provider>
    );
  } else {
    return (
      <Provider store={store}>
        <LumeraPersistGate>
          <DYORServerSyncGate>
            <ConnectedContainer {...otherProps} />
          </DYORServerSyncGate>
        </LumeraPersistGate>
      </Provider>
    );
  }
};
